import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  subAdminList,
  subAdminDetail,
  createSubAdmin,
  updateSubAdmin,
} from "./callbacks";
import { QData } from "../../../types/basic-type-list";
import { ISubAdminPayload } from "../../../types/api/subAdmin";
import { TResponseWithQData } from "../../../types/common";

export const SUB_ADMIN_KEYS = {
  all: ["sub-admins"] as const,
  lists: () => [...SUB_ADMIN_KEYS.all, "list"] as const,
  list: (qData: QData) => [...SUB_ADMIN_KEYS.lists(), qData] as const,
  details: () => [...SUB_ADMIN_KEYS.all, "detail"] as const,
  detail: (id: string) => [...SUB_ADMIN_KEYS.details(), id] as const,
};

export function useSubAdminList(
  qData: QData & { name?: string; email?: string },
  options?: any,
) {
  return useQuery<TResponseWithQData<ISubAdminPayload[]>>({
    queryKey: SUB_ADMIN_KEYS.list(qData),
    queryFn: async () => {
      const { data } = await subAdminList(qData);
      return data;
    },
    ...options,
  });
}

export function useSubAdminDetail(id: string, options?: any) {
  return useQuery({
    queryKey: SUB_ADMIN_KEYS.detail(id),
    queryFn: async () => {
      const { data } = await subAdminDetail(id);
      return data;
    },
    enabled: !!id,
    ...options,
  });
}

export function useSubAdminCreate({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (err: any) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSubAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUB_ADMIN_KEYS.lists() });
      onSuccess?.();
    },
    onError: (err: any) => onError?.(err),
  });
}

export function useSubAdminUpdate({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (err: any) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSubAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUB_ADMIN_KEYS.lists() });
      onSuccess?.();
    },
    onError: (err: any) => onError?.(err),
  });
}
